import { useTranslation } from "react-i18next";
import { RiArrowRightSLine, RiVerifiedBadgeLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { Link } from "~/components/ui/link";
import type { FleetContractBannerContent } from "~/data/products/types";

interface FleetContractBannerProps {
  content: FleetContractBannerContent;
}

// "Stuck in a contract with another vendor?" card (Figma 10480:408117 mobile, 10480:408960
// desktop): stacked and centred on mobile, one row with the CTA on the right from lg. Not
// FleetBannerSection: no photo, a lime headline and a badge line. Must render inside
// the .fleet token scope.
export function FleetContractBanner({ content }: FleetContractBannerProps) {
  const { t } = useTranslation("products");

  return (
    <section aria-labelledby="fleet-contract-banner-title" className="wrap pb-6 lg:pb-12">
      <div className="flex flex-col gap-6 rounded-2xl bg-[var(--background-alt)] px-4 py-8 lg:flex-row lg:items-center lg:justify-between lg:px-[60px] lg:py-6">
        <div className="flex flex-col items-center gap-2 text-center lg:items-start lg:text-left">
          <h2
            id="fleet-contract-banner-title"
            className="font-display text-xl leading-[1.15] font-extrabold text-[#b2ff0d] lg:text-[32px]"
          >
            {t(content.headline)}
          </h2>
          <div className="flex flex-col items-center gap-2 lg:items-start lg:gap-4">
            <p className="text-sm leading-5 text-[#d6d8db] lg:text-base lg:leading-[1.5]">{t(content.body)}</p>
            <p className="text-bouncie-200 flex items-center gap-2 text-xs leading-[18px] font-bold">
              <RiVerifiedBadgeLine aria-hidden className="size-6" />
              {t(content.badge)}
            </p>
          </div>
        </div>
        <Button
          asChild
          variant="secondary-alt"
          size="sm"
          className="h-11 w-full text-base leading-5 tracking-[0.16px] lg:h-[52px] lg:w-auto lg:px-6"
          iconRight={<RiArrowRightSLine className="size-5" />}
        >
          <Link data-sid="fleet-contract-banner-cta" to={content.ctaHref}>
            {t(content.ctaLabel)}
          </Link>
        </Button>
      </div>
    </section>
  );
}
